// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-B.yaml
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-C.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13480
import type { SkuAttributeRow } from '../queries/useGetSkuAttributeList'
import { withParticle } from './particle'
import { MAX_LENGTHS, PRICE_MAX, PRICE_SCALE } from './consts'

const LABELS: Partial<Record<keyof SkuAttributeRow, string>> = {
  barcode: '바코드',
  item_code: '상품코드',
  item_option: '상품옵션',
  item_name: '상품명',
  category: '카테고리',
  color: '색상',
  style_number: '스타일번호',
  unit_code: '상품단위',
  brand_id: '브랜드ID',
  brand_name: '브랜드명',
}

const priceError = (value: number | null, label: string): string | null => {
  if (value == null) return null
  if (!Number.isFinite(value)) return `${withParticle(label, '이', '가')} 숫자가 아닙니다.`
  if (value < 0) return `${withParticle(label, '은', '는')} 0 이상이어야 합니다.`
  if (Math.abs(value) > PRICE_MAX) return `${withParticle(label, '은', '는')} ${PRICE_MAX.toLocaleString()} 이하여야 합니다.`
  const decimals = (String(value).split('.')[1] ?? '').length
  if (decimals > PRICE_SCALE) return `${withParticle(label, '은', '는')} 소수점 ${PRICE_SCALE}자리까지만 입력할 수 있습니다.`
  return null
}

/**
 * 저장 전 행 검증 — **BE 와 같은 기준이다** (`RegisterSkuAttributeService.validate`).
 *
 * ### 왜 화면에서도 검사하는가 — 채번이 되돌릴 수 없기 때문이다
 *
 * BE 도 같은 검사를 하지만 그쪽은 요청이 도착한 뒤다. 화면에서 먼저 막으면 사용자가 업로드 전에
 * 고치고, 무엇보다 **폭을 넘는 행이 SCM Hub 로 나가지 않는다.** prod `sql_mode` 가 비-STRICT 라
 * 그대로 두면 DB 가 잘라 넣는데 Hub 는 안 잘린 값으로 채번해 두 시스템의 키가 영구히 어긋난다.
 *
 * ### 둘 중 하나만 두지 않는 이유
 *
 * 화면 검증은 우회할 수 있고(직접 API 호출), BE 검증은 사용자에게 늦게 닿는다. **둘 다 필요하다.**
 * 기준이 갈리지 않게 상수를 같은 값으로 두고 양쪽 주석에 서로를 적어 둔다.
 *
 * @return 첫 오류 메시지. 통과하면 `null`
 */
export const validateRow = (row: SkuAttributeRow): string | null => {
  if (row._isNew && !row.barcode.trim()) return '바코드는 필수입니다.'

  // 키를 행 타입으로 좁혀 순회한다 — `Record<string, …>` 였을 때는 키를 `unit_cd` 처럼 잘못
  // 적어도 `undefined` → `''` → length 0 이 되어 **검증이 조용히 통과**했고 컴파일도 테스트도
  // 잡지 못했다. 좁힌 덕에 `row` 를 `Record<string, unknown>` 으로 캐스팅할 필요도 없어졌다
  for (const [key, max] of Object.entries(MAX_LENGTHS) as [keyof SkuAttributeRow, number][]) {
    const value = String(row[key] ?? '')
    if (value.length > max) {
      return `${withParticle(LABELS[key] ?? key, '은', '는')} ${max}자 이하여야 합니다. (현재 ${value.length}자)`
    }
  }

  return priceError(row.receivable_price, '입고단가') ?? priceError(row.payable_price, '출고단가')
}
